import { isBefore, isEqual, isValid, parseISO, startOfDay } from 'date-fns';

/**
 * Inlined rather than imported: a shared lib cannot depend on an app's local helpers, so this
 * mirrors Register's `util/helpers/string` `isBlank` exactly (null/undefined/empty/whitespace-only).
 */
const isBlank = (value: string | null | undefined): boolean => !value || value.trim() === '';

export const parseExpirationDate = (value: string): Date => {
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : new Date(value);
};

/**
 * Whether an MMJ ID's expiration has passed, by the same day-granularity rule the backend uses.
 *
 * Mirrors `dbo.isMMJIDExpired`'s day-granularity comparison; `ThroughMMJIdExpiry`
 * (`isThroughMmjIdExpiryEnabled`) picks the day boundary: off (the DB default) means the
 * expiration day itself is already expired, on means the ID is valid *through* that day.
 *
 * Shared between Register and Mobile Checkout, both of which feed the same check-in that enforces
 * this unconditionally. Note: this uses the device's local clock, not the location's configured
 * timezone like the backend does.
 */
export const isMmjIdExpired = (
  expirationDate: string | null | undefined,
  isThroughMmjIdExpiryEnabled: boolean,
): boolean => {
  if (isBlank(expirationDate) || !expirationDate) {
    return false;
  }

  const parsedExpirationDate = parseExpirationDate(expirationDate);
  // Unparseable/invalid dates are deliberately not treated as expired, to avoid bad legacy data
  // creating a new checkout-blocking failure mode.
  if (!isValid(parsedExpirationDate)) {
    return false;
  }

  const expirationDay = startOfDay(parsedExpirationDate);
  const today = startOfDay(new Date());
  return isBefore(expirationDay, today) || (!isThroughMmjIdExpiryEnabled && isEqual(expirationDay, today));
};
